import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from '@/shared/utils/cn';

const cardVariants = cva(
  "rounded-[10px] border transition-all duration-200",
  {
    variants: {
      variant: {
        default: "bg-card border-border shadow-card hover:shadow-card-hover transition-all duration-200",
        glass: "bg-card dark:bg-[rgba(10,16,30,0.65)] dark:backdrop-blur-[24px] dark:saturate-[1.3] border-border dark:border-[rgba(46,134,171,0.08)] shadow-card",
        elevated: "bg-card shadow-elevated border-border/50",
        neon: "bg-card border-primary/20 shadow-glow-sm hover:border-primary/40",
        mesh: "bg-gradient-to-br from-card via-card to-primary/5 border-border/30",
      },
      hover: {
        none: "",
        lift: "hover:translate-y-[-2px] hover:shadow-card-hover hover:border-primary/30 transition-transform duration-200",
        glow: "hover:shadow-card-hover hover:border-primary/30",
      },
    },
    defaultVariants: {
      variant: "default",
      hover: "none",
    },
  }
);

export interface CardProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof cardVariants> {}

const Card = React.forwardRef<HTMLDivElement, CardProps>(
  ({ className, variant, hover, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(cardVariants({ variant, hover }), className)}
      {...props}
    />
  )
);
Card.displayName = "Card";

const CardHeader = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn("flex flex-col space-y-1.5 p-5", className)} {...props} />
  ),
);
CardHeader.displayName = "CardHeader";

const CardTitle = React.forwardRef<HTMLParagraphElement, React.HTMLAttributes<HTMLHeadingElement>>(
  ({ className, ...props }, ref) => (
    <h3 ref={ref} className={cn("text-sm font-medium leading-none tracking-tight", className)} {...props} />
  ),
);
CardTitle.displayName = "CardTitle";

const CardDescription = React.forwardRef<HTMLParagraphElement, React.HTMLAttributes<HTMLParagraphElement>>(
  ({ className, ...props }, ref) => (
    <p ref={ref} className={cn("text-sm text-muted-foreground", className)} {...props} />
  ),
);
CardDescription.displayName = "CardDescription";

const CardContent = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => <div ref={ref} className={cn("p-5 pt-0", className)} {...props} />,
);
CardContent.displayName = "CardContent";

const CardFooter = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn("flex items-center p-5 pt-0", className)} {...props} />
  ),
);
CardFooter.displayName = "CardFooter";

export { Card, CardHeader, CardFooter, CardTitle, CardDescription, CardContent };
